import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Bell, Megaphone, PackageCheck, ArrowRightLeft, ShoppingCart, CalendarOff, MessageSquare, AlertTriangle, CheckCheck, ChevronRight, CalendarPlus, CalendarClock, Inbox, FileCheck2, Truck, Receipt, PlugZap, ShieldAlert } from "lucide-react";

export function notifView(n) {
  if (n.type === "order_authorisation" || n.type === "order_query")
    return { Icon: ShoppingCart, to: n.link || "/orders", title: n.type === "order_query" ? `Order #${n.order_no} queried` : `Order #${n.order_no} awaiting authorisation`, sub: `${n.message || "Needs your sign-off"}${n.by ? ` · by ${n.by}` : ""}` };
  if (n.type === "absence_authorisation")
    return { Icon: CalendarOff, to: n.link || "/absences", title: "Absence awaiting approval", sub: `${n.message || "Needs your approval"}${n.by ? ` · by ${n.by}` : ""}` };
  if (n.type === "feedback")
    return { Icon: MessageSquare, to: n.link || "/docs", title: n.message || "Feedback update", sub: n.by ? `by ${n.by}` : "Tester feedback" };
  if (n.type === "run_disruption")
    return { Icon: AlertTriangle, to: n.link || "/scheduler", title: n.task_title || "Run disruption", sub: `${n.to_department ? `${n.to_department} · ` : ""}${n.by ? `by ${n.by}` : ""}`, note: n.handover_note };
  const ICONS = { job_new: CalendarPlus, job_change: CalendarClock, sales_enquiry_new: Inbox, sales_customer_reply: MessageSquare,
    sales_quote_decision: FileCheck2, load_signed_off: Truck, fin_refund: Receipt, admin_integration_failure: PlugZap, attachment_scan_alert: ShieldAlert,
    load_ready: Truck, load_started: Truck, load_missing: AlertTriangle, load_discrepancy: AlertTriangle, load_assigned: Truck,
    pick_assigned: PackageCheck, vehicle_assignment: Truck, ticket_new: MessageSquare, ticket_update: MessageSquare,
    sales_quote_followup: FileCheck2, engineer_late: CalendarClock, reminder: Bell, escalation: ShieldAlert,
    job_pick_conflict: AlertTriangle, job_low_stock: PackageCheck };
  if (ICONS[n.type])
    return { Icon: ICONS[n.type], to: n.entity?.link || n.link || "/", title: n.task_title || "Notification", sub: n.message, note: null };
  return {
    Icon: ArrowRightLeft,
    to: n.task_legacy_id ? `/tasks/${n.task_legacy_id}` : (n.link || "/"),
    title: n.task_legacy_id && n.to_department ? `${n.task_legacy_id} → ${n.to_department}` : (n.task_title || n.message || "Notification"),
    sub: n.to_department ? `${n.from_department ? `From ${n.from_department} · ` : ""}${n.by ? `by ${n.by}` : ""}${n.assignee_name ? ` · assigned to ${n.assignee_name}` : ""}` : (n.task_title ? n.message : ""),
    note: n.handover_note ? `“${n.handover_note}”` : null,
  };
}

function when(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d) ? "" : d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function Item({ Icon, tone = "slate", to, title, sub, note, ts, onOpen, testid, priority, action }) {
  const tones = { slate: "bg-slate-100 text-slate-600", amber: "bg-amber-100 text-amber-700", indigo: "bg-indigo-100 text-indigo-700" };
  const pr = { critical: "bg-rose-600 text-white", high: "bg-amber-500 text-white" }[priority];
  return (
    <Link to={to} onClick={onOpen} data-testid={testid}
      className="group flex items-start gap-3 rounded-xl px-3 py-3 hover:bg-slate-50 transition-colors">
      <span className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}><Icon className="w-4 h-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#1D1D1F]">{title}{pr && <span className={`ml-2 align-middle text-[9px] font-bold uppercase rounded px-1.5 py-0.5 ${pr}`}>{priority}</span>}{action && <span className="ml-2 align-middle text-[9px] font-semibold rounded-full px-1.5 py-0.5 bg-indigo-100 text-indigo-700">Action</span>}</div>
        {sub && <div className="text-[12px] text-[#6E6E73] mt-0.5">{sub}</div>}
        {note && <div className="text-[12px] text-slate-600 mt-1 line-clamp-2">{note}</div>}
        {ts && <div className="text-[11px] text-slate-400 mt-1">{when(ts)}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 mt-2 shrink-0" />
    </Link>
  );
}

export function NotificationsPanel({ notifs, anns, returnsCount, onChanged }) {
  const [open, setOpen] = useState(false);
  const unreadNotifs = notifs.items.filter((n) => !n.read);
  const total = notifs.unread + anns.length + (returnsCount > 0 ? 1 : 0);

  const close = () => setOpen(false);
  const readNotif = (id) => { close(); api.post(`/notifications/${id}/read`).then(onChanged).catch(() => {}); };
  const readAnn = (id) => { close(); api.post(`/announcements/${id}/read`).then(onChanged).catch(() => {}); };
  const readAll = () => {
    Promise.all([
      notifs.unread ? api.post("/notifications/read-all") : null,
      anns.length ? api.post("/announcements/read-all") : null,
    ]).then(onChanged).catch(() => {});
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="notifications-bell" title="Notifications"
        className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
        <Bell className="w-[18px] h-[18px]" />
        {total > 0 && (
          <span data-testid="notifications-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="notifications-panel">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
            <SheetTitle className="text-lg">Notifications</SheetTitle>
            <SheetDescription data-testid="notifications-panel-count">{total ? `${total} unread` : "You're all caught up"}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-2">
            {total === 0 && <div className="py-16 text-center text-sm text-slate-400" data-testid="notifications-empty">No unread notifications.</div>}
            {returnsCount > 0 && (
              <Item Icon={PackageCheck} tone="amber" to="/returns" onOpen={close} testid="notification-returns"
                title={`${returnsCount} return${returnsCount === 1 ? "" : "s"} to inspect`} sub="Customer returns awaiting inspection" />
            )}
            {anns.map((a) => (
              <Item key={a.id} Icon={Megaphone} tone="indigo" to="/" onOpen={() => readAnn(a.id)} testid={`announcement-notif-${a.id}`}
                title={a.title} sub={a.author_name} note={a.body} ts={a.created_at} />
            ))}
            {unreadNotifs.map((n) => {
              const v = notifView(n);
              return <Item key={n.id} {...v} to={n.entity?.link || v.to} priority={n.priority} action={n.kind === "action" && n.action_status === "open"} ts={n.ts} onOpen={() => readNotif(n.id)} testid={`notification-${n.id}`} />;
            })}
          </div>
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
            <Link to="/notifications" onClick={close} data-testid="notifications-view-all" className="text-sm font-medium text-[#1D1D1F] hover:underline">View all</Link>
          {(notifs.unread + anns.length) > 0 && (
              <button onClick={readAll} data-testid="notifications-mark-all-read"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline">
                <CheckCheck className="w-4 h-4" /> Mark all as read
              </button>
          )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
